import { useCallback, useEffect, useState } from "preact/hooks";

import { deletePasskey, fetchPasskeys } from "../lib/api";
import type { PasskeyOut } from "../types";

/**
 * Sprint-007 D6 — the Settings "Your sign-in" screen's passkey list (UX
 * §4.2). Mirrors `hooks/useAnimals.ts`'s shape (`{data, loading, error,
 * refresh}` plus one mutation helper) — always enabled (every authed role
 * reaches "Your sign-in" now that every role has a password, UX §4's own
 * "not role-gated the way Projects is").
 */
export function usePasskeys() {
  const [passkeys, setPasskeys] = useState<PasskeyOut[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const rows = await fetchPasskeys();
      setPasskeys(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load your quick sign-in list.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const remove = useCallback(
    async (passkeyId: string) => {
      await deletePasskey(passkeyId);
      await refresh();
    },
    [refresh],
  );

  return { passkeys, loading, error, refresh, remove };
}
